/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import {
  EstimatedDurationSource,
  EstimatedDurationType,
} from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface EstimatedDurationProps {
  id: string;
  asOfTime: Date;
  durationInMinutes: number;
  isOfficial: boolean;
  estimateSource: EstimatedDurationSource;
  durationType: EstimatedDurationType;
  ownerId: string;
  tripId: string;
  positionReportId: string | null;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class EstimatedDurationRecord extends Record<EstimatedDurationProps>({
  id: null as any,
  asOfTime: null as any,
  durationInMinutes: null as any,
  isOfficial: null as any,
  estimateSource: null as any,
  durationType: null as any,
  ownerId: null as any,
  tripId: null as any,
  positionReportId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    asOfTime: string | number | Date;
    durationInMinutes: number;
    isOfficial: boolean;
    estimateSource: EstimatedDurationSource;
    durationType: EstimatedDurationType;
    ownerId: string;
    tripId: string;
    positionReportId: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): EstimatedDurationRecord {
    if (json == null) {
      throw new Error('EstimatedDurationRecord.fromJS: parameter cannot be null');
    }
    return new EstimatedDurationRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'EstimatedDurationJson.id',
      ),
      asOfTime: new Date(
        getFromOrThrow(
          json,
          (j) => propValueOrDefault(j, 'asOfTime', '0001-01-01T00:00:00.00Z'),
          'EstimatedDurationJson.asOfTime',
        ),
      ),
      durationInMinutes: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'durationInMinutes', 0),
        'EstimatedDurationJson.durationInMinutes',
      ),
      isOfficial: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isOfficial', false),
        'EstimatedDurationJson.isOfficial',
      ),
      estimateSource: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'estimateSource', EstimatedDurationSource.Pilot),
        'EstimatedDurationJson.estimateSource',
      ),
      durationType: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'durationType', EstimatedDurationType.Waypoint),
        'EstimatedDurationJson.durationType',
      ),
      ownerId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'ownerId', '00000000-0000-0000-0000-000000000000'),
        'EstimatedDurationJson.ownerId',
      ),
      tripId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'tripId', '00000000-0000-0000-0000-000000000000'),
        'EstimatedDurationJson.tripId',
      ),
      positionReportId: json.positionReportId,
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'EstimatedDurationJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    asOfTime: string | number | Date;
    durationInMinutes: number;
    isOfficial: boolean;
    estimateSource: EstimatedDurationSource;
    durationType: EstimatedDurationType;
    ownerId: string;
    tripId: string;
    positionReportId: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [EstimatedDurationRecord, Error[]] {
    if (json == null) {
      throw new Error('EstimatedDurationRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new EstimatedDurationRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'EstimatedDurationJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      asOfTime: tryOrDefault(
        () =>
          new Date(
            getFromOrThrow(
              json,
              (j) => propValueOrDefault(j, 'asOfTime', '0001-01-01T00:00:00.00Z'),
              'EstimatedDurationJson.asOfTime',
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      durationInMinutes: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'durationInMinutes', 0),
            'EstimatedDurationJson.durationInMinutes',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isOfficial: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isOfficial', false),
            'EstimatedDurationJson.isOfficial',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      estimateSource: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'estimateSource', EstimatedDurationSource.Pilot),
            'EstimatedDurationJson.estimateSource',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      durationType: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'durationType', EstimatedDurationType.Waypoint),
            'EstimatedDurationJson.durationType',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ownerId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'ownerId', '00000000-0000-0000-0000-000000000000'),
            'EstimatedDurationJson.ownerId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      tripId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'tripId', '00000000-0000-0000-0000-000000000000'),
            'EstimatedDurationJson.tripId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      positionReportId: tryOrDefault(
        () => json.positionReportId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'EstimatedDurationJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: EstimatedDurationProps) {
    super(props);
    if (props == null) {
      throw new Error('EstimatedDurationRecord constructor parameter cannot be null');
    }
  }
}

export abstract class EstimatedDurationViewModelBase implements EstimatedDurationProps {
  protected readonly _props: EstimatedDurationProps;
  private _json?: EstimatedDurationProps;

  get id(): string {
    return this._props.id;
  }
  get asOfTime(): Date {
    return this._props.asOfTime;
  }
  get durationInMinutes(): number {
    return this._props.durationInMinutes;
  }
  get isOfficial(): boolean {
    return this._props.isOfficial;
  }
  get estimateSource(): EstimatedDurationSource {
    return this._props.estimateSource;
  }
  get durationType(): EstimatedDurationType {
    return this._props.durationType;
  }
  get ownerId(): string {
    return this._props.ownerId;
  }
  get tripId(): string {
    return this._props.tripId;
  }
  get positionReportId(): string | null {
    return this._props.positionReportId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isGps() {
    return this._props.estimateSource === EstimatedDurationSource.Gps;
  }
  public get isPilot() {
    return this._props.estimateSource === EstimatedDurationSource.Pilot;
  }
  public get isTripLeg() {
    return this._props.durationType === EstimatedDurationType.TripLeg;
  }
  public get isWaypoint() {
    return this._props.durationType === EstimatedDurationType.Waypoint;
  }

  public isAnyEstimateSource(...estimatedDurationSource: EstimatedDurationSource[]) {
    return estimatedDurationSource.includes(this._props.estimateSource);
  }
  public isAnyDurationType(...estimatedDurationType: EstimatedDurationType[]) {
    return estimatedDurationType.includes(this._props.durationType);
  }

  constructor(props: EstimatedDurationProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        asOfTime: this._props.asOfTime,
        durationInMinutes: this._props.durationInMinutes,
        isOfficial: this._props.isOfficial,
        estimateSource: this._props.estimateSource,
        durationType: this._props.durationType,
        ownerId: this._props.ownerId,
        tripId: this._props.tripId,
        positionReportId: this._props.positionReportId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllEstimatedDurationEntities = (state: AppState) =>
  state.entityStores.estimatedDuration.entities;

export const selectEstimatedDurationById = (id: string) => (state: AppState) =>
  state.entityStores.estimatedDuration.entities.get(id);

export const selectEstimatedDurationMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.estimatedDuration.meta.get(id);

export const selectEstimatedDurationAndMetadataById = (id: string) =>
  createSelector(
    selectEstimatedDurationById(id),
    selectEstimatedDurationMetadataById(id),
    (estimatedDuration, meta) => ({ estimatedDuration, meta }),
  );

export function selectEstimatedDurationEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllEstimatedDurationEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, EstimatedDurationRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectEstimatedDurationEntitiesByAsOfTime(asOfTime: Date) {
  return createSelector(selectAllEstimatedDurationEntities, (entities) =>
    entities.filter((e) => e.asOfTime === asOfTime).valueSeq(),
  );
}

export function selectEstimatedDurationEntitiesByDurationInMinutes(durationInMinutes: number) {
  return createSelector(selectAllEstimatedDurationEntities, (entities) =>
    entities.filter((e) => e.durationInMinutes === durationInMinutes).valueSeq(),
  );
}

export function selectEstimatedDurationEntitiesByIsOfficial(isOfficial: boolean) {
  return createSelector(selectAllEstimatedDurationEntities, (entities) =>
    entities.filter((e) => e.isOfficial === isOfficial).valueSeq(),
  );
}

export function selectEstimatedDurationEntitiesByEstimateSource(
  estimateSource: EstimatedDurationSource,
) {
  return createSelector(selectAllEstimatedDurationEntities, (entities) =>
    entities.filter((e) => e.estimateSource === estimateSource).valueSeq(),
  );
}

export function selectEstimatedDurationEntitiesByDurationType(durationType: EstimatedDurationType) {
  return createSelector(selectAllEstimatedDurationEntities, (entities) =>
    entities.filter((e) => e.durationType === durationType).valueSeq(),
  );
}

export function selectEstimatedDurationEntitiesByOwnerId(ownerId: string) {
  return createSelector(selectAllEstimatedDurationEntities, (entities) =>
    entities.filter((e) => e.ownerId === ownerId).valueSeq(),
  );
}

export function selectEstimatedDurationEntitiesByTripId(tripId: string) {
  return createSelector(selectAllEstimatedDurationEntities, (entities) =>
    entities.filter((e) => e.tripId === tripId).valueSeq(),
  );
}

export function selectEstimatedDurationEntitiesByPositionReportId(positionReportId: string) {
  return createSelector(selectAllEstimatedDurationEntities, (entities) =>
    entities.filter((e) => e.positionReportId === positionReportId).valueSeq(),
  );
}

export function selectEstimatedDurationEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllEstimatedDurationEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
